import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardMedia, CardContent, Grid, Checkbox,
  CircularProgress, Chip, Alert, Paper, Stack, Divider,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { uploadAPI, wardrobeAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';

interface FitItem {
  id: number;
  name: string;
  category: string;
  subcategory?: string;
  brand?: string;
  image_url: string;
  matched_from?: string;
  match_score?: number;
  in_photo?: boolean;
}

const FitOfTheDay: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useUI();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyzed, setAnalyzed] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageS3Key, setImageS3Key] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);

  const [plannedItems, setPlannedItems] = useState<FitItem[]>([]);
  const [plannedLabel, setPlannedLabel] = useState<string | null>(null);
  const [additionalItems, setAdditionalItems] = useState<FitItem[]>([]);
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [wornToday, setWornToday] = useState<FitItem[]>([]);

  const todayISO = new Date().toISOString().slice(0, 10);

  const loadWornToday = async () => {
    try {
      const res = await wardrobeAPI.getItems();
      const items: any[] = res.data?.items || res.data || [];
      setWornToday(items.filter((i) => i.last_worn && String(i.last_worn).slice(0, 10) === todayISO));
    } catch {
      /* non-fatal */
    }
  };

  // Pre-populate from any outfit already planned for today (calendar / daily / chat)
  useEffect(() => {
    (async () => {
      try {
        const res = await wardrobeAPI.getPlannedFit();
        if (res.data?.planned && Array.isArray(res.data.items) && res.data.items.length > 0) {
          const items: FitItem[] = res.data.items;
          setPlannedItems(items);
          setSelectedIds(new Set(items.map((i) => i.id)));
          setPlannedLabel(res.data.outfit_name || "Today's planned outfit");
        }
      } catch {
        /* no planned fit is fine */
      }
    })();
    loadWornToday();
    loadHistory();
  }, []);

  async function loadHistory() {
    try {
      const res = await wardrobeAPI.getFitHistory();
      setHistory(res.data?.history || []);
    } catch {
      /* non-fatal */
    }
  }

  const handleFile = async (file: File) => {
    setError(null);
    setPreview(URL.createObjectURL(file));
    setAnalyzed(false);
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (plannedItems.length > 0) {
        formData.append('expected_item_ids', JSON.stringify(plannedItems.map((i) => i.id)));
      }
      const res = await uploadAPI.analyzeFit(formData);

      const planned: FitItem[] = res.data.planned_items || [];
      const additional: FitItem[] = res.data.additional_items || [];
      setImageUrl(res.data.image_url || null);
      setImageS3Key(res.data.s3_key || null);
      setUnmatched(res.data.unmatched_detected || []);

      if (planned.length > 0) {
        // We had a planned outfit: keep it (confirmed by the photo), additions are opt-in.
        setPlannedItems(planned);
        setAdditionalItems(additional);
        setSelectedIds(new Set(planned.map((i) => i.id)));
      } else {
        // No planned outfit: the detected matches ARE the fit (pre-selected).
        setPlannedItems([]);
        setAdditionalItems(additional);
        setSelectedIds(new Set(additional.map((i) => i.id)));
      }
      setAnalyzed(true);

      if (planned.length === 0 && additional.length === 0) {
        toast("Couldn't match any of your wardrobe items in this photo", 'info');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to analyze the photo. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleMarkWorn = async () => {
    if (selectedIds.size === 0) return;
    setSaving(true);
    try {
      const ids = Array.from(selectedIds);
      await wardrobeAPI.markWorn({ item_ids: ids, image_url: imageUrl || undefined, image_s3_key: imageS3Key || undefined });
      toast(`Logged today's fit — marked ${ids.length} item${ids.length === 1 ? '' : 's'} as worn`, 'success', { duration: 5000 });
      navigate('/wardrobe');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to mark items as worn.');
    } finally {
      setSaving(false);
    }
  };

  const renderItemCard = (item: FitItem, variant: 'planned' | 'additional') => {
    const selected = selectedIds.has(item.id);
    return (
      <Grid size={{ xs: 6, sm: 4 }} key={`${variant}-${item.id}`}>
        <Card
          variant="outlined"
          onClick={() => toggle(item.id)}
          sx={{
            cursor: 'pointer', position: 'relative',
            outline: '2px solid', outlineColor: selected ? 'primary.main' : 'transparent',
            opacity: selected ? 1 : 0.6, transition: 'all 0.15s ease',
          }}
        >
          <Checkbox
            checked={selected}
            icon={variant === 'additional' ? <AddCircleOutlineIcon /> : <span />}
            checkedIcon={<CheckCircleIcon />}
            sx={{ position: 'absolute', top: 4, right: 4, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '50%', p: 0.25 }}
          />
          {variant === 'planned' && analyzed && (
            <Chip
              size="small"
              label={item.in_photo ? 'In photo' : 'Not seen'}
              color={item.in_photo ? 'success' : 'default'}
              sx={{ position: 'absolute', top: 6, left: 6, height: 20, fontSize: '0.62rem' }}
            />
          )}
          <CardMedia
            component="img"
            image={item.image_url}
            alt={item.name}
            sx={{ height: 150, objectFit: 'cover', objectPosition: 'center top', bgcolor: '#f5f5f5' }}
          />
          <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>{item.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {item.subcategory || item.category}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const hasContent = plannedItems.length > 0 || (analyzed && additionalItems.length > 0);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Fit of the Day</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload a selfie of today's outfit. We'll confirm the pieces from your wardrobe and mark them worn.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {wornToday.length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon fontSize="small" color="success" /> Already worn today
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {wornToday.map((i) => (
              <Box key={i.id} sx={{ width: 64, textAlign: 'center' }}>
                <Box
                  component="img"
                  src={i.image_url}
                  alt={i.name}
                  sx={{ width: 64, height: 64, objectFit: 'cover', objectPosition: 'center top', borderRadius: 1.5, bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}
                />
                <Typography variant="caption" noWrap sx={{ display: 'block', mt: 0.25 }}>{i.name}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed',
              minHeight: 320, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 1.5,
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {preview ? (
              <Box component="img" src={preview} alt="Today's fit"
                sx={{ width: '100%', maxHeight: 420, objectFit: 'contain', borderRadius: 1 }} />
            ) : (
              <>
                <CameraAltIcon sx={{ fontSize: 56, color: 'text.secondary' }} />
                <Typography variant="subtitle1">Tap to upload your fit photo</Typography>
                <Typography variant="caption" color="text.secondary">A full-body photo works best</Typography>
              </>
            )}
          </Paper>
          {preview && (
            <Button fullWidth sx={{ mt: 1 }} onClick={() => fileInputRef.current?.click()} disabled={analyzing}>
              Choose a different photo
            </Button>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          {analyzing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 6, justifyContent: 'center' }}>
              <CircularProgress size={28} />
              <Typography>Analyzing your fit…</Typography>
            </Box>
          )}

          {!analyzing && !hasContent && (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>
                {plannedItems.length === 0
                  ? 'Upload a photo and your matched wardrobe items will appear here.'
                  : ''}
              </Typography>
            </Box>
          )}

          {!analyzing && (
            <>
              {plannedItems.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {plannedLabel || "Today's planned outfit"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                    {analyzed
                      ? 'Confirmed against your photo — uncheck anything you didn’t actually wear.'
                      : 'Already planned for today. Upload a photo to confirm.'}
                  </Typography>
                  <Grid container spacing={2}>
                    {plannedItems.map((item) => renderItemCard(item, 'planned'))}
                  </Grid>
                </Box>
              )}

              {analyzed && additionalItems.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {plannedItems.length > 0 && <Divider sx={{ mb: 2 }} />}
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {plannedItems.length > 0 ? 'Also spotted in your photo' : `Found ${additionalItems.length} item${additionalItems.length === 1 ? '' : 's'} from your wardrobe`}
                  </Typography>
                  {plannedItems.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                      Not part of the planned outfit — tap to add any you also wore.
                    </Typography>
                  )}
                  <Grid container spacing={2}>
                    {additionalItems.map((item) => renderItemCard(item, 'additional'))}
                  </Grid>
                </Box>
              )}

              {analyzed && unmatched.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Also detected (not in your wardrobe): {unmatched.join(', ')}
                </Typography>
              )}

              {hasContent && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                  <Button
                    variant="contained" size="large" startIcon={<CheckCircleIcon />}
                    disabled={selectedIds.size === 0 || saving}
                    onClick={handleMarkWorn}
                  >
                    {saving ? 'Saving…' : `Mark ${selectedIds.size} as worn today`}
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {history.length > 0 && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Fit history</Typography>
          <Grid container spacing={2}>
            {history.map((fit) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={fit.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  {fit.image_url && (
                    <CardMedia
                      component="img"
                      image={fit.image_url}
                      alt={`Fit on ${fit.date}`}
                      sx={{ height: 260, objectFit: 'cover', objectPosition: 'center top', bgcolor: '#f5f5f5' }}
                    />
                  )}
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {fit.date ? new Date(fit.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {(fit.items || []).map((it: any) => (
                        it.image_url ? (
                          <Box
                            key={it.id}
                            component="img"
                            src={it.image_url}
                            alt={it.name}
                            title={it.name}
                            sx={{ width: 40, height: 40, objectFit: 'cover', objectPosition: 'center top', borderRadius: 1, border: '1px solid rgba(0,0,0,0.08)' }}
                          />
                        ) : (
                          <Chip key={it.id} label={it.name} size="small" variant="outlined" />
                        )
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default FitOfTheDay;
